import { useEffect, useRef, useState } from "react";
import { PageNav } from "@/components/ui/page-nav";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { useAuth, useOrganization } from "@clerk/react";
import { toast } from "sonner";
import Papa from "papaparse";
import { useOrganizationSettingsStore } from "@/stores";
import type { ExclusionType } from "@/stores/settings/organizationSettingsStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TypeToConfirm } from "@/components/ui/type-to-confirm";
import { ChipsInput } from "@/components/ui/chips-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Trash2Icon,
  SearchIcon,
  ListXIcon,
} from "lucide-react";
import { CsvColumnPickerDialog } from "./CsvColumnPickerDialog";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameFooter,
} from "@/components/ui/frame";
import EmptyState from "@/components/ui/empty-state";

const EXCLUSION_CAP = 2048;
const PAGE_SIZE = 10;

const detectType = (value: string): ExclusionType => {
  const v = value.trim().toLowerCase();
  if (!v) return "username";
  if (v.startsWith("*") || v.includes("@")) return "email";
  return "username";
};

const typeLabel = (type: ExclusionType) =>
  type === "email" ? "Email" : "Username";

export function ExclusionListSection() {
  const { orgId, userId, getToken } = useAuth();
  const { organization } = useOrganization();
  const {
    excludedEntries,
    excludedEntriesCount,
    excludedEntriesLoading,
    fetchExcludedEntries,
    addToExcludedEntries,
    removeFromExcludedEntries,
    clearExcludedEntries,
  } = useOrganizationSettingsStore();

  const [csvData, setCsvData] = useState<string[][]>([]);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearConfirmName, setClearConfirmName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearConfirmPhrase = `${organization?.name ?? ""} Exclusion List`.trim();
  const canConfirmClear = clearConfirmName === clearConfirmPhrase;

  const handleClearDialogClose = (open: boolean) => {
    setClearDialogOpen(open);
    if (!open) {
      setClearConfirmName("");
    }
  };

  const filteredEntries = searchQuery
    ? excludedEntries.filter((entry) =>
      entry.value.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    : excludedEntries;

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE));
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => {
    const loadData = async () => {
      if (!orgId) return;
      const token = await getToken();
      if (token) {
        fetchExcludedEntries(orgId, token);
      }
    };
    loadData();
  }, [orgId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      complete: (results) => {
        const data = results.data as string[][];
        const filtered = data.filter((row) => row.some((cell) => cell?.trim()));
        if (filtered.length < 2) {
          toast.error("CSV must have a header row and at least one data row.");
          return;
        }
        setCsvData(filtered);
        setColumnPickerOpen(true);
      },
      error: () => {
        toast.error("Failed to parse CSV file.");
      },
    });

    e.target.value = "";
  };

  const handleColumnConfirm = async (values: string[], type: ExclusionType) => {
    if (!orgId || !userId) return;

    const uniqueValues = [...new Set(values)];
    if (!uniqueValues.length) {
      toast.error("No values found in the selected column.");
      return;
    }

    setUploading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const result = await addToExcludedEntries(
        orgId,
        userId,
        uniqueValues,
        type,
        token,
      );

      if (result.success) {
        const noun = type === "email" ? "email" : "username";
        let message = `Added ${result.added} ${noun}${result.added !== 1 ? "s" : ""}`;
        if (result.duplicates_skipped) {
          message += `, ${result.duplicates_skipped} duplicate${result.duplicates_skipped !== 1 ? "s" : ""} skipped`;
        }
        toast.success(message);
        await fetchExcludedEntries(orgId, token);
      } else {
        toast.error(result.error || "Failed to add entries.");
      }
    } finally {
      setUploading(false);
      setCsvData([]);
    }
  };

  const handleManualAdd = async (value: string) => {
    if (!orgId || !userId) return;
    setAdding(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("Auth token unavailable.");

      const type = detectType(value);
      const result = await addToExcludedEntries(
        orgId,
        userId,
        [value],
        type,
        token,
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to add entry.");
      }

      if (result.added && result.added > 0) {
        toast.success(`Added ${value} as ${typeLabel(type)}`);
        await fetchExcludedEntries(orgId, token);
        return;
      }

      if (result.duplicates_skipped) {
        throw new Error(`${value} is already in the list.`);
      }

      throw new Error(
        type === "email"
          ? "Invalid email format. Use email@example.com or *example.com."
          : "Invalid value.",
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (entry: {
    value: string;
    type: ExclusionType;
  }) => {
    if (!orgId) return;
    const key = `${entry.type}:${entry.value}`;
    setRemoving(key);
    try {
      const token = await getToken();
      if (!token) return;

      const success = await removeFromExcludedEntries(
        orgId,
        [entry.value],
        entry.type,
        token,
      );

      if (success) {
        toast.success(`Removed ${entry.value}`);
        await fetchExcludedEntries(orgId, token);
      } else {
        toast.error("Failed to remove entry.");
      }
    } finally {
      setRemoving(null);
    }
  };

  const handleClearAll = async () => {
    if (!orgId) return;

    setClearing(true);
    try {
      const token = await getToken();
      if (!token) return;

      const success = await clearExcludedEntries(orgId, token);
      if (success) {
        toast.success("Exclusion list cleared.");
        handleClearDialogClose(false);
      } else {
        toast.error("Failed to clear exclusion list.");
      }
    } finally {
      setClearing(false);
    }
  };

  if (excludedEntriesLoading && !excludedEntries.length) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Frame>
        <FrameHeader>
          <div className="flex items-center gap-2">
            <FrameTitle>Exclusion List</FrameTitle>
            <span
              className={`font-mono ml-auto text-sm ${excludedEntriesCount >= EXCLUSION_CAP ? "text-destructive" : "text-foreground"}`}
            >
              {excludedEntriesCount} / {EXCLUSION_CAP}
            </span>
          </div>
          <FrameDescription>Creators Amy never contacts or finds. Use *domain.com to block a whole domain.</FrameDescription>
        </FrameHeader>

        <FramePanel className="flex lg:flex-row justify-between items-center gap-20">
          <div className="flex items-center gap-2">
            <ChipsInput
              items={[]}
              onAdd={handleManualAdd}
              onRemove={() => { }}
              addLabel="Add manually"
              placeholder="manual enrty . . ."
              className="w-64 h-8"
            />
            {adding && (
              <div className="flex items-center bg-background gap-1.5 ml-2 text-xs text-muted-foreground animate-in fade-in-0 z-10 duration-100">
                <Spinner className="size-3.5" />
                Adding...
              </div>
            )}
          </div>
          <ScopeWrapper>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="w-64 h-8.5 bg-background hover:bg-muted shadow-none border-dashed border-muted-foreground/30 cursor-pointer"
              disabled={uploading}
            />
          </ScopeWrapper>
        </FramePanel>

        {excludedEntries.length > 0 && (
          <>
            <FramePanel>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <InputGroup className="h-8 w-64">
                    <InputGroupInput
                      placeholder={`Search ${excludedEntriesCount} entries...`}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                    <InputGroupAddon>
                      <SearchIcon className="size-3.5" />
                    </InputGroupAddon>
                  </InputGroup>
                </div>
                <AlertDialog
                  open={clearDialogOpen}
                  onOpenChange={handleClearDialogClose}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive-ghost"
                      size="sm"
                      disabled={clearing}
                      className="[&_svg]:size-3.5"
                    >
                      {clearing ? (
                        <Spinner />
                      ) : (
                        <Trash2Icon className="opacity-60" />
                      )}
                      Clear All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-lg">
                    <AlertDialogHeader variant="destructive">
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will remove all {excludedEntriesCount} entries from
                        your exclusion list. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>

                    <TypeToConfirm
                      phrase={clearConfirmPhrase}
                      value={clearConfirmName}
                      onValueChange={setClearConfirmName}
                      onEnter={handleClearAll}
                      disabled={clearing}
                    />

                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={clearing}>Cancel</AlertDialogCancel>
                      <Button
                        variant="destructive"
                        disabled={clearing || !canConfirmClear}
                        onClick={handleClearAll}
                      >
                        {clearing ? (
                          <Spinner />
                        ) : (
                          <Trash2Icon />
                        )}
                        Clear All
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </FramePanel>
            <Table variant="card">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-64">Value</TableHead>
                  <TableHead className="w-16">Type</TableHead>
                  <TableHead className="w-16">Date Added</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedEntries.map((entry) => {
                  const key = `${entry.type}:${entry.value}`;
                  const isRemoving = removing === key;
                  return (
                    <TableRow key={entry._id}>
                      <TableCell className="font-medium">
                        {entry.value}
                      </TableCell>
                      <TableCell className="capitalize text-xs font-mono text-muted-foreground">
                        {entry.type}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {entry.added_at
                          ? new Date(entry.added_at).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="destructive-ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemove(entry)}
                            disabled={isRemoving}
                          >
                            {isRemoving ? (
                              <Spinner className="size-3.5" />
                            ) : (
                              <Trash2Icon className="size-3.5" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <FrameFooter>
              <PageNav
                page={currentPage}
                pageCount={totalPages}
                total={filteredEntries.length}
                pageSize={PAGE_SIZE}
                onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
                onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              />
            </FrameFooter>
          </>
        )}

        {!uploading &&
          !excludedEntriesLoading &&
          excludedEntries.length === 0 && (
            <FramePanel>
              <EmptyState
                className="mb-10"
                icon={<ListXIcon />}
                text="No excluded entries yet. Add one above or upload a CSV."
              />
            </FramePanel>
          )}

        {uploading && (
          <FramePanel className="flex flex-col items-center gap-3 py-8 text-center text-xs text-muted-foreground">
            <Spinner className="size-4" />
            Uploading and processing your CSV. This may take a moment.
          </FramePanel>
        )}
      </Frame>

      <CsvColumnPickerDialog
        open={columnPickerOpen}
        onOpenChange={setColumnPickerOpen}
        csvData={csvData}
        onConfirm={handleColumnConfirm}
      />
    </div>
  );
}
